import React, { useMemo } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Switch, Row, Col, Alert } from 'antd';

const perM = (v: any) => {
  if (!v) return undefined;
  const num = parseFloat(String(v));
  if (isNaN(num)) return String(v);
  return String(num * 1_000_000);
};

const perToken = (v: any) => {
  if (!v || v === '') return null;
  const num = parseFloat(String(v));
  if (isNaN(num)) return String(v);
  return String(num / 1_000_000);
};

const priceFields = ['promptPrice', 'completionPrice', 'inputCacheReadPrice', 'imagePrice', 'webSearchPrice', 'internalReasoningPrice'];

export const LlmModelEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm({ resource: 'llm-models' });
  const record = queryResult?.data?.data as any;

  const convertedFormProps = useMemo(() => {
    if (!formProps.initialValues) return formProps;
    const vals = { ...formProps.initialValues };
    for (const f of priceFields) {
      if (vals[f] != null) vals[f] = perM(vals[f]);
    }
    return { ...formProps, initialValues: vals };
  }, [formProps.initialValues]);

  const handleSubmit = (values: any) => {
    for (const f of priceFields) {
      values[f] = perToken(values[f]);
    }
    return formProps.onFinish?.(values);
  };

  if (record?.source === 'OPENROUTER') {
    return (
      <Edit resource="llm-models" saveButtonProps={{ style: { display: 'none' } }}>
        <Alert
          type="info"
          showIcon
          message="This model is managed by OpenRouter sync"
          description="OpenRouter models cannot be edited directly. Clone this model to create an editable copy with your own pricing."
          style={{ marginBottom: 16 }}
        />
        <div style={{ color: '#888' }}>
          <p><strong>Slug:</strong> {record.slug}</p>
          <p><strong>Name:</strong> {record.name}</p>
          <p><strong>Source:</strong> {record.source}</p>
        </div>
      </Edit>
    );
  }

  return (
    <Edit resource="llm-models" saveButtonProps={saveButtonProps}>
      <Form {...convertedFormProps} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label="Slug" name="slug">
          <Input placeholder="openai/gpt-4o" />
        </Form.Item>
        <Form.Item label="Name" name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Context Length" name="contextLength">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Max Completion Tokens" name="maxCompletionTokens">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Tokenizer" name="tokenizer">
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Instruct Type" name="instructType">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <h4>Pricing ($/1M tokens)</h4>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Prompt" name="promptPrice">
              <Input placeholder="0.065" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Completion" name="completionPrice">
              <Input placeholder="0.26" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Cache Read" name="inputCacheReadPrice">
              <Input placeholder="0.01" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Image" name="imagePrice">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Web Search" name="webSearchPrice">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Reasoning" name="internalReasoningPrice">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Provider" name="providerId">
              <Input placeholder="Provider UUID (optional)" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
